import React, { useEffect, useRef, useState } from 'react';
import {
  Camera,
  Check,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  Mail,
  Monitor,
  Moon,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  Sun,
  User as UserIcon,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { OtpInput } from 'components/ui/otp-input';
import { AuthPhoneField } from 'components/auth';
import { SessionsCard } from 'components/settings/SessionsCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { cn } from 'lib/utils';
import { isCompleteUzPhone } from 'lib/phone';
import { useToast } from 'components/ui/use-toast';
import { useAuthContext } from 'modules/auth/context';
import { useLanguageStore, type Language } from 'store/language.store';
import { useThemeStore, type Theme } from 'store/theme.store';
import * as AuthApi from 'modules/auth/api';
import * as AuthMappers from 'modules/auth/mappers';
import {
  useStartPhoneSetup,
  useVerifyPhoneSetup,
  useRemovePhone,
} from 'modules/auth/hooks';

const LANGUAGES: { code: Language; label: string }[] = [
  { code: 'uz', label: "O'zbek" },
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Русский' },
];

const THEME_ORDER: Theme[] = ['light', 'dark', 'system'];
const THEME_ICON: Record<Theme, LucideIcon> = { light: Sun, dark: Moon, system: Monitor };

const MAX_AVATAR_SIZE = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

function getInitials(nameStr?: string): string {
  if (!nameStr) return 'U';
  const parts = nameStr.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}

/** The member's own account — personal, so no permission gate. */
const ProfileSettings: React.FC = () => {
  const { t } = useTranslation();
  const { state, methods } = useAuthContext();
  const { user, role } = state;
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Profile state
  const [name, setName] = useState(user?.name ?? '');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Theme & Language
  const { theme, setTheme } = useThemeStore();
  const { language, setLanguage } = useLanguageStore();

  // Phone setup state & mutations
  const startPhoneMutation = useStartPhoneSetup();
  const verifyPhoneMutation = useVerifyPhoneSetup();
  const removePhoneMutation = useRemovePhone();

  const [phoneStep, setPhoneStep] = useState<'idle' | 'awaiting_code'>('idle');
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneHint, setPhoneHint] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneCooldown, setPhoneCooldown] = useState(0);

  useEffect(() => {
    if (phoneCooldown <= 0) return;
    const id = setInterval(() => setPhoneCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [phoneCooldown]);

  // Release the previous preview blob whenever it's replaced/unmounted.
  useEffect(() => {
    return () => {
      if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    };
  }, [avatarPreview]);

  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      toast.error(t('invalid_file_type', 'Invalid file type'), {
        description: t('please_upload_a_jpeg_png_webp_or_gif_image', 'Please upload a JPEG, PNG, WebP or GIF image.'),
      });
      return;
    }
    if (file.size > MAX_AVATAR_SIZE) {
      toast.error(t('file_too_large', 'File too large'), {
        description: t('avatar_must_be_5_mb_or_smaller', 'Avatar must be 5 MB or smaller.'),
      });
      return;
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleSaveProfile = async () => {
    if (!name.trim()) {
      toast.error(t('name_required', 'Name is required'), {
        description: t('full_name_cannot_be_empty', 'Full name cannot be empty.'),
      });
      return;
    }
    setSavingProfile(true);
    try {
      let avatarUrl = user?.avatarUrl;
      if (avatarFile) {
        const { data } = await AuthApi.UploadAvatar(fileInputRef.current?.files?.[0] || avatarFile);
        avatarUrl = data.avatarUrl;
      }
      const { data } = await AuthApi.UpdateProfile({ name: name.trim(), avatarUrl });
      methods.setUser(AuthMappers.User(data));
      setAvatarFile(null);
      toast.success(t('profile_saved', 'Profil saqlandi'));
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(t('failed_to_save_profile', 'Failed to save profile'), {
        description: e?.response?.data?.message || 'Please try again.',
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error(t('all_fields_required', 'All fields are required'));
      return;
    }
    if (newPassword.length < 8) {
      toast.error(t('password_too_short', 'Password is too short'), {
        description: t('new_password_must_be_at_least_8_characters', 'New password must be at least 8 characters.'),
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('passwords_do_not_match', 'Passwords do not match'));
      return;
    }
    setSavingPassword(true);
    try {
      await AuthApi.ChangePassword({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success(t('password_changed_successfully', 'Password changed successfully'));
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(t('failed_to_change_password', 'Failed to change password'), {
        description: e?.response?.data?.message || 'Please try again.',
      });
    } finally {
      setSavingPassword(false);
    }
  };

  // Phone actions
  const handleStartPhone = async () => {
    try {
      const data = await startPhoneMutation.mutateAsync({ phone: phoneInput });
      setPhoneHint(data.phoneHint);
      setPhoneStep('awaiting_code');
      setPhoneCooldown(60);
      toast.success(t('profile_phone_code_sent_title', 'Code sent'), {
        description: t('profile_phone_code_sent_desc', { defaultValue: 'Code sent to {{phone}}', phone: data.phoneHint }),
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(t('profile_phone_send_failed_title', 'Could not send code'), {
        description: err?.response?.data?.message || t('please_try_again', 'Please try again.'),
      });
    }
  };

  const handleVerifyPhone = async (codeValue: string) => {
    if (verifyPhoneMutation.isPending) return;
    try {
      await verifyPhoneMutation.mutateAsync({ code: codeValue });
      setPhoneStep('idle');
      setPhoneInput('');
      setPhoneCode('');
      setPhoneHint('');
      toast.success(t('profile_phone_verified_title', 'Phone verified'), {
        description: t('profile_phone_verified_toast_desc', 'You can now sign in faster with phone OTP.'),
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(t('profile_phone_verify_failed_title', 'Verification failed'), {
        description: err?.response?.data?.message || t('please_try_again', 'Please try again.'),
      });
      setPhoneCode('');
    }
  };

  const handleResendPhone = async () => {
    if (!phoneInput) return;
    try {
      const data = await startPhoneMutation.mutateAsync({ phone: phoneInput });
      setPhoneHint(data.phoneHint);
      setPhoneCooldown(60);
      toast.success(t('profile_phone_code_sent_title', 'Code sent'));
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(t('profile_phone_resend_failed_title', 'Could not resend code'), {
        description: err?.response?.data?.message || t('please_try_again', 'Please try again.'),
      });
    }
  };

  const handleRemovePhone = async () => {
    if (
      typeof window !== 'undefined' &&
      !window.confirm(
        t('profile_phone_remove_confirm', 'Remove your phone number? You will no longer be able to sign in via phone OTP.')
      )
    ) {
      return;
    }
    try {
      await removePhoneMutation.mutateAsync();
      toast.success(t('profile_phone_removed', 'Phone removed'));
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(t('profile_phone_remove_failed_title', 'Could not remove phone'), {
        description: err?.response?.data?.message || t('please_try_again', 'Please try again.'),
      });
    }
  };

  const displayAvatar = avatarPreview ?? user?.avatarUrl;
  const profileDirty = name.trim() !== (user?.name ?? '').trim() || !!avatarFile;

  // Role reads like "ORG ADMIN" in pill badge
  const roleLabel = role
    ? role.toUpperCase().replace(/_/g, ' ')
    : null;

  const themeLabel: Record<Theme, string> = {
    light: t('usermenu_themelight', "Yorug'"),
    dark: t('usermenu_themedark', "Qorong'i"),
    system: t('usermenu_themesystem', 'Tizim'),
  };

  // Password rules validation
  const hasLength = newPassword.length >= 8;
  const hasCase = /[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword);
  const hasNumber = /\d/.test(newPassword);
  const strengthScore = [hasLength, hasCase, hasNumber].filter(Boolean).length;

  const phoneVerified = !!user?.phone && !!user?.isPhoneVerified;
  const canManagePhone = user?.canManagePhone ?? false;

  return (
    <div className="space-y-6">
      {/* Top Breadcrumb & Page Title */}
      <div className="space-y-1">
        <p className="text-[13px] font-semibold text-muted-foreground">
          {t('settings.account', 'Hisob sozlamalari')}
        </p>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
          {t('profile', 'Profil')}
        </h1>
      </div>

      {/* Hero Profile Banner */}
      <div className="rounded-lg border border-border bg-card p-6 sm:p-7 shadow-sm">
        <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-6 text-center sm:text-left">
          {/* Squircle Avatar with Camera Button */}
          <div className="relative shrink-0">
            <div className="w-20 h-20 sm:w-22 sm:h-22 rounded-lg overflow-hidden bg-primary text-primary-foreground flex items-center justify-center font-bold text-2xl sm:text-3xl">
              {displayAvatar ? (
                <img
                  src={displayAvatar}
                  alt={name || user?.name || 'User'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span>{getInitials(name || user?.name)}</span>
              )}
            </div>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              aria-label={t('change_photo', 'Rasmni o\'zgartirish')}
              className="absolute -bottom-1 -right-1 flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full border border-border bg-background text-foreground shadow-sm transition-transform hover:scale-110 active:scale-95"
            >
              <Camera size={14} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={handleAvatarSelect}
            />
          </div>

          {/* User Info & Role Badge */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2.5">
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground truncate">
                {name || user?.name}
              </h2>
              {roleLabel && (
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground">
                  <ShieldCheck size={13} className="shrink-0" />
                  {roleLabel}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs sm:text-sm text-muted-foreground">
              {t('profile_header_subtitle', 'Rasmni o\'zgartirish — JPEG, PNG, WebP yoki GIF · maks. 5 MB')}
            </p>
          </div>
        </div>
      </div>

      {/* Shaxsiy ma'lumotlar (Personal Info) Card */}
      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <div className="p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted/80 text-foreground">
              <UserIcon size={18} className="text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold tracking-tight text-foreground">
                {t('profile_personal_info', 'Shaxsiy ma\'lumotlar')}
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                {t('profile_personal_info_desc', 'Ismingiz va aloqa ma\'lumotlaringiz — tashkilot a\'zolari shuni ko\'radi.')}
              </p>
            </div>
          </div>

          <div className="mt-6 space-y-5">
            {/* 2-column: Name and Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
              <div className="space-y-2">
                <label
                  htmlFor="profile-name"
                  className="block text-[13px] font-medium text-muted-foreground"
                >
                  {t('full_name', 'TO\'LIQ ISM')}
                </label>
                <Input
                  id="profile-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('your_name', 'Ismingizni kiriting')}
                  className="h-11 rounded-xl bg-background border-border px-3.5 focus-visible:ring-1"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="profile-email"
                  className="block text-[13px] font-medium text-muted-foreground"
                >
                  {t('email_address', 'EMAIL MANZIL')}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="profile-email"
                    type="email"
                    value={user?.email ?? '—'}
                    className="h-11 pl-10 rounded-xl bg-muted/40 text-muted-foreground border-border cursor-not-allowed"
                    disabled
                  />
                </div>
                <p className="text-xs text-muted-foreground flex items-center gap-1.5 pt-0.5">
                  <Lock size={12} className="shrink-0 text-muted-foreground/80" />
                  <span>{t('email_cannot_be_changed', 'Email manzilni o\'zgartirib bo\'lmaydi')}</span>
                </p>
              </div>
            </div>

            {/* Phone Identity Box */}
            <div className="rounded-xl border border-border bg-muted/30 p-4 transition-colors">
              {phoneVerified && phoneStep === 'idle' ? (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm">
                      <Smartphone size={18} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm sm:text-base font-semibold text-foreground tracking-tight">
                          {user?.phone}
                        </p>
                        <span className="inline-flex items-center gap-1 rounded-full bg-success-50 px-2 py-0.5 text-xs font-medium text-success-700 dark:bg-success-900/30 dark:text-success-300">
                          <ShieldCheck size={12} className="shrink-0" />
                          {t('verified', 'TASDIQLANGAN')}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {canManagePhone
                          ? t('profile_phone_verified_hint_manage', 'Tasdiqlangan — SMS orqali yuborilgan kod bilan kirishingiz mumkin.')
                          : t('profile_phone_verified_hint_locked', 'Bu sizning kirish raqamingiz — uni o\'zgartirib yoki o\'chirib bo\'lmaydi.')}
                      </p>
                    </div>
                  </div>

                  {canManagePhone && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRemovePhone}
                      disabled={removePhoneMutation.isPending}
                      className="rounded-xl shrink-0"
                    >
                      {removePhoneMutation.isPending && <Loader2 size={14} className="mr-1.5 animate-spin" />}
                      {t('remove', 'O\'chirish')}
                    </Button>
                  )}
                </div>
              ) : phoneStep === 'awaiting_code' ? (
                <div className="space-y-3.5">
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t('profile_phone_sent_to_prefix', 'Biz 6 xonali kodni yubordik:')}{' '}
                    <span className="font-semibold text-foreground">{phoneHint}</span>.{' '}
                    {t('profile_phone_sent_to_suffix', 'Uni quyida kiriting.')}
                  </p>
                  <div className="flex justify-center py-2">
                    <OtpInput
                      value={phoneCode}
                      onChange={setPhoneCode}
                      onComplete={handleVerifyPhone}
                      disabled={verifyPhoneMutation.isPending}
                      autoFocus
                      ariaInvalid={verifyPhoneMutation.isError}
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                    {phoneCooldown > 0 ? (
                      <span>{t('profile_phone_resend_in', { defaultValue: 'Resend code in {{seconds}}s', seconds: phoneCooldown })}</span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendPhone}
                        disabled={startPhoneMutation.isPending}
                        className="font-semibold text-foreground underline underline-offset-[3px] hover:decoration-2 disabled:opacity-50"
                      >
                        {t('profile_phone_resend', 'Kodni qayta yuborish')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setPhoneStep('idle');
                        setPhoneCode('');
                        setPhoneHint('');
                      }}
                      className="hover:text-foreground font-medium"
                    >
                      {t('cancel', 'Bekor qilish')}
                    </button>
                  </div>
                  <Button
                    onClick={() => handleVerifyPhone(phoneCode)}
                    disabled={verifyPhoneMutation.isPending || phoneCode.length !== 6}
                    size="sm"
                    className="w-full rounded-xl"
                  >
                    {verifyPhoneMutation.isPending && <Loader2 size={14} className="mr-2 animate-spin" />}
                    {t('profile_phone_verify_cta', 'Telefonni tasdiqlash')}
                  </Button>
                </div>
              ) : canManagePhone ? (
                <div className="space-y-3">
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t('profile_phone_add_desc', 'SMS orqali bir martalik kod bilan tezroq kirish uchun telefon raqamini qo\'shing.')}
                  </p>
                  <AuthPhoneField
                    label={t('phone_number', 'Telefon raqami')}
                    value={phoneInput}
                    onChange={setPhoneInput}
                  />
                  <div className="flex justify-end pt-1">
                    <Button
                      onClick={handleStartPhone}
                      disabled={startPhoneMutation.isPending || !isCompleteUzPhone(phoneInput)}
                      size="sm"
                      className="rounded-xl"
                    >
                      {startPhoneMutation.isPending && <Loader2 size={14} className="mr-1.5 animate-spin" />}
                      {t('profile_phone_send_code', 'Kod yuborish')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm">
                    <Smartphone size={18} />
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t('profile_phone_locked_desc', 'Telefon raqamingiz hisobingizga bog\'langan va uni o\'zgartirib bo\'lmaydi.')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Card Footer */}
        <div className="flex justify-end border-t border-border/50 bg-muted/20 px-6 py-4">
          <Button
            onClick={handleSaveProfile}
            disabled={savingProfile || !profileDirty}
            className="rounded-xl px-6 h-10 font-semibold text-sm shadow-sm transition-all"
          >
            {savingProfile && <Loader2 size={14} className="mr-2 animate-spin" />}
            {t('save_profile', 'Profilni saqlash')}
          </Button>
        </div>
      </div>

      {/* Parolni o'zgartirish (Change Password) Card */}
      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <div className="p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted/80 text-foreground">
              <KeyRound size={18} className="text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold tracking-tight text-foreground">
                {t('change_password', 'Parolni o\'zgartirish')}
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                {t('profile_password_desc', 'Kuchli parol hisobingizni himoya qiladi — uni hech kim bilan bo\'lishmang.')}
              </p>
            </div>
          </div>

          <div className="mt-6 space-y-5">
            {/* Current Password Field */}
            <div className="space-y-2">
              <label
                htmlFor="currentPassword"
                className="block text-[13px] font-medium text-muted-foreground"
              >
                {t('current_password', 'JORIY PAROL')}
              </label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder={t('enter_current_password', 'Joriy parolni kiriting')}
                  className="h-11 rounded-xl bg-background border-border pr-10 focus-visible:ring-1"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((p) => !p)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* 2-column: New Password & Confirm Password */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
              <div className="space-y-2">
                <label
                  htmlFor="newPassword"
                  className="block text-[13px] font-medium text-muted-foreground"
                >
                  {t('new_password', 'YANGI PAROL')}
                </label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('min_8_characters', 'Kamida 8 belgi')}
                    className="h-11 rounded-xl bg-background border-border pr-10 focus-visible:ring-1"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew((p) => !p)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="confirmPassword"
                  className="block text-[13px] font-medium text-muted-foreground"
                >
                  {t('confirm_new_password', 'YANGI PAROLNI TASDIQLANG')}
                </label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('repeat_new_password', 'Yangi parolni takrorlang')}
                    className={cn(
                      'h-11 rounded-xl bg-background border-border pr-10 focus-visible:ring-1',
                      confirmPassword && confirmPassword !== newPassword
                        ? 'border-destructive focus-visible:ring-destructive'
                        : '',
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((p) => !p)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {confirmPassword && confirmPassword !== newPassword && (
                  <p className="text-xs text-destructive">{t('passwords_do_not_match', 'Passwords do not match')}</p>
                )}
              </div>
            </div>

            {/* Password Strength Progress Bar & Rules Checklist */}
            <div className="rounded-xl border border-border/70 bg-muted/20 p-4 space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 flex-1">
                  <div
                    className={cn(
                      'h-1.5 flex-1 rounded-full transition-all duration-300',
                      strengthScore >= 1
                        ? strengthScore === 1
                          ? 'bg-amber-500'
                          : strengthScore === 2
                          ? 'bg-lime-500'
                          : 'bg-primary'
                        : 'bg-muted-foreground/20',
                    )}
                  />
                  <div
                    className={cn(
                      'h-1.5 flex-1 rounded-full transition-all duration-300',
                      strengthScore >= 2
                        ? strengthScore === 2
                          ? 'bg-lime-500'
                          : 'bg-primary'
                        : 'bg-muted-foreground/20',
                    )}
                  />
                  <div
                    className={cn(
                      'h-1.5 flex-1 rounded-full transition-all duration-300',
                      strengthScore >= 3 ? 'bg-primary' : 'bg-muted-foreground/20',
                    )}
                  />
                </div>
                <span className="text-xs font-semibold text-muted-foreground shrink-0">
                  {t('profile_password_strength', 'Parol kuchi')}
                </span>
              </div>

              {/* 3 Criteria Pills */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-1 text-xs">
                <div
                  className={cn(
                    'flex items-center gap-2 transition-colors',
                    hasLength ? 'text-foreground font-medium' : 'text-muted-foreground',
                  )}
                >
                  <div
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-all',
                      hasLength ? 'border-success-600 bg-success-600 text-white' : 'border-muted-foreground/40',
                    )}
                  >
                    {hasLength ? <Check size={10} strokeWidth={3} /> : null}
                  </div>
                  <span>{t('profile_password_rule_length', 'Kamida 8 belgi')}</span>
                </div>

                <div
                  className={cn(
                    'flex items-center gap-2 transition-colors',
                    hasCase ? 'text-foreground font-medium' : 'text-muted-foreground',
                  )}
                >
                  <div
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-all',
                      hasCase ? 'border-success-600 bg-success-600 text-white' : 'border-muted-foreground/40',
                    )}
                  >
                    {hasCase ? <Check size={10} strokeWidth={3} /> : null}
                  </div>
                  <span>{t('profile_password_rule_case', 'Katta va kichik harf')}</span>
                </div>

                <div
                  className={cn(
                    'flex items-center gap-2 transition-colors',
                    hasNumber ? 'text-foreground font-medium' : 'text-muted-foreground',
                  )}
                >
                  <div
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-all',
                      hasNumber ? 'border-success-600 bg-success-600 text-white' : 'border-muted-foreground/40',
                    )}
                  >
                    {hasNumber ? <Check size={10} strokeWidth={3} /> : null}
                  </div>
                  <span>{t('profile_password_rule_number', 'Kamida bitta raqam')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Card Footer */}
        <div className="flex justify-end border-t border-border/50 bg-muted/20 px-6 py-4">
          <Button
            onClick={handleChangePassword}
            disabled={
              savingPassword ||
              !currentPassword ||
              !newPassword ||
              !confirmPassword ||
              newPassword !== confirmPassword ||
              newPassword.length < 8
            }
            className="rounded-xl px-6 h-10 font-semibold text-sm shadow-sm transition-all"
          >
            {savingPassword && <Loader2 size={14} className="mr-2 animate-spin" />}
            {t('update_password', 'Parolni yangilash')}
          </Button>
        </div>
      </div>

      {/* Faol seanslar (Active Sessions) Card */}
      <SessionsCard />

      {/* Afzalliklar (Preferences) Card */}
      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <div className="p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted/80 text-foreground">
              <SlidersHorizontal size={18} className="text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold tracking-tight text-foreground">
                {t('settings.nav_preferences', 'Afzalliklar')}
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                {t('profile_preferences_desc', 'Interfeys ko\'rinishi va tilini o\'zingizga moslang.')}
              </p>
            </div>
          </div>

          <div className="mt-6 space-y-6">
            {/* Theme Row */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-[13px] font-medium text-muted-foreground">
                  {t('usermenu_theme', 'MAVZU')}
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                  {t('profile_theme_desc', 'Yorug\', qorong\'i yoki tizim sozlamasiga ergashadi.')}
                </p>
              </div>

              <div className="inline-flex items-center rounded-full border border-border bg-muted/40 p-1 self-start sm:self-auto">
                {THEME_ORDER.map((opt) => {
                  const Icon = THEME_ICON[opt];
                  const active = theme === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setTheme(opt)}
                      aria-pressed={active}
                      title={themeLabel[opt]}
                      className={cn(
                        'flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all',
                        active
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <Icon size={14} aria-hidden />
                      <span>{themeLabel[opt]}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-border/60" />

            {/* Interface Language Row */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-[13px] font-medium text-muted-foreground">
                  {t('settings.display_language', 'INTERFEYS TILI')}
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                  {t('profile_language_desc', 'Butun ilova matnlari tanlangan tilda ko\'rsatiladi.')}
                </p>
              </div>

              <Select value={language} onValueChange={(v) => setLanguage(v as Language)}>
                <SelectTrigger className="w-full sm:w-44 rounded-xl border border-border bg-background h-10 px-3.5 text-sm font-medium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl border border-border">
                  {LANGUAGES.map((l) => (
                    <SelectItem key={l.code} value={l.code} className="rounded-lg">
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
